import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDoc, getDocs, query, where, Timestamp
} from 'firebase/firestore';
import { db } from '../firebase-config';

const casesCollection = collection(db, 'cases');

/**
 * Live pre-write check that a matter-assignment target uid is real,
 * same-firm, and active — mirrors the firestore.rules isEligibleAssignee
 * gate so callers get a clear, specific rejection reason instead of a raw
 * permission-denied error. The rules gate remains the authoritative
 * backstop regardless of this check; this never substitutes another user
 * or a display-name fallback for an invalid uid — callers must reject and
 * preserve the existing assignment on {eligible: false}.
 */
export const validateCaseAssignee = async (uid, firmId) => {
  if (!uid) return { eligible: true, user: null };
  try {
    const userSnap = await getDoc(doc(db, 'users', uid));
    if (!userSnap.exists()) return { eligible: false, reason: 'This user no longer exists.' };
    const u = userSnap.data();
    if (u.firmId !== firmId) return { eligible: false, reason: 'This user belongs to a different firm.' };
    if (u.status !== 'active') return { eligible: false, reason: 'This user is not active.' };
    return { eligible: true, user: { id: uid, displayName: u.displayName || u.email || 'Unknown' } };
  } catch (error) {
    return { eligible: false, reason: 'Could not verify this assignee — please try again.' };
  }
};

export const createCase = async (userId, caseData, firmId) => {
  try {
    const docRef = await addDoc(casesCollection, {
      ...caseData,
      userId,
      firmId: firmId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getCases = async (userId, firmId) => {
  try {
    let cases = [];
    if (firmId) {
      const q = query(casesCollection, where('firmId', '==', firmId));
      const snapshot = await getDocs(q);
      snapshot.forEach((d) => cases.push({ id: d.id, ...d.data() }));
    }
    // No creator-field (userId-only) fallback: firm data is read ONLY through
    // the current firm's tenant-scoped query above. A userId-only query cannot
    // be proven against the active-membership rules and is denied outright;
    // a firm-constrained variant would be a strict subset of the query above.
    // Deduplicate
    cases = cases.filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i);
    cases.sort((a, b) => {
      const dateA = a.createdAt?.toDate() || new Date(0);
      const dateB = b.createdAt?.toDate() || new Date(0);
      return dateB - dateA;
    });
    return { success: true, cases };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateCase = async (caseId, updates) => {
  try {
    const docRef = doc(db, 'cases', caseId);
    await updateDoc(docRef, { ...updates, updatedAt: Timestamp.now() });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteCase = async (caseId) => {
  try {
    const docRef = doc(db, 'cases', caseId);
    await deleteDoc(docRef);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};